import { computed } from '@angular/core';
import { ScrapperResponse } from '@contler/assistant/lib/models/scrapper-response.model';
import { HotelModel } from '@contler/core/hotel';
import { setDefaultMaterialColors } from '@contler/ui';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';

export interface FaqItem {
  question: string;
  answer: string;
}

export interface BrandingData {
  logo?: string;
  faqs: FaqItem[];
  mainColor: string;
  secondaryColor: string;
  logoFile: File | null;
  hotelName: string;
  fullName: string;
  role: string;
  email: string;
  numberOfRooms: number;
  phoneNumber: string;
}

export interface CreatedHotelData {
  hotel: HotelModel;
  token: string;
}
export interface HomeGuestData {
  headerColor: string;
  homeColor: string;
}

export enum OnboardingStep {
  INITIAL = 'initial',
  LOADING = 'loading',
  RESULTS = 'results',
  CONFIGURATION = 'configuration',
}

export interface OnboardingState {
  currentStep: OnboardingStep;
  websiteUrl: string | null;
  scrapedData: ScrapperResponse | null;
  brandingData: BrandingData;
  isLoading: boolean;
  error: string | null;
  faqs: FaqItem[];
  createdHotelData: CreatedHotelData | null;
  homeGuestData: HomeGuestData | null;
}

const initialState: OnboardingState = {
  currentStep: OnboardingStep.INITIAL,
  websiteUrl: null,
  scrapedData: null,
  brandingData: {
    faqs: [],
    mainColor: '',
    secondaryColor: '',
    logoFile: null,
    hotelName: '',
    fullName: '',
    role: '',
    email: '',
    numberOfRooms: 0,
    phoneNumber: '',
  },
  isLoading: false,
  error: null,
  faqs: [],
  createdHotelData: null,
  homeGuestData: null,
};

export const OnboardingStore = signalStore(
  {
    providedIn: 'root',
  },
  withState(initialState),
  withHooks({
    onInit: (store) => {
      const fullData = localStorage.getItem('onboarding-data');
      if (fullData) {
        const parsedData = JSON.parse(fullData);
        patchState(store, parsedData);
        if (parsedData?.brandingData?.mainColor) {
          setDefaultMaterialColors(parsedData.brandingData.mainColor);
        }
      }
    },
  }),
  withComputed((store) => ({
    // Computed signals for better reactivity
    hasScrapedData: computed(() => !!store.scrapedData()),
    hasError: computed(() => !!store.error()),
    canProceed: computed(() => store.scrapedData() && !store.isLoading()),
    hasBrandingData: computed(() => store.brandingData().faqs.length > 0 || !!store.brandingData().logo),
    fullData: computed(() => ({
      currentStep: store.currentStep(),
      scrapedData: store.scrapedData(),
      brandingData: store.brandingData(),
      isLoading: store.isLoading(),
      error: store.error(),
      faqs: store.faqs(),
      createdHotelData: store.createdHotelData(),
      websiteUrl: store.websiteUrl(),
      hasScrapedData: !!store.scrapedData(),
      hasError: !!store.error(),
      canProceed: store.scrapedData() && !store.isLoading(),
      hasBrandingData: store.brandingData().faqs.length > 0 || !!store.brandingData().logo,
      homeGuestData: store.homeGuestData(),
    })),
  })),
  withMethods((store) => ({
    // Actions
    setWebsiteUrl(url: string) {
      patchState(store, {
        websiteUrl: url,
        error: null,
      });
    },

    setLoading(loading: boolean) {
      patchState(store, {
        isLoading: loading,
        currentStep: loading ? OnboardingStep.LOADING : store.currentStep(),
      });
    },

    setScrapedData(data: ScrapperResponse) {
      patchState(store, {
        scrapedData: data,
        isLoading: false,
        currentStep: OnboardingStep.RESULTS,
        error: null,
        faqs:
          data.frequentAskedQuestions?.map((faq) => ({
            question: faq.question,
            answer: faq.answer,
          })) || [],
        brandingData: {
          ...store.brandingData(),
          logo: data.hotelLogo,
          mainColor: data.hotelColors?.[0] || '',
          secondaryColor: data.hotelColors?.[1] || '',
        },
        homeGuestData: {
          headerColor: data.hotelColors?.[0] || '',
          homeColor: data.hotelColors?.[1] || '',
        },
      });
    },

    setError(error: string) {
      patchState(store, {
        error,
        isLoading: false,
        currentStep: OnboardingStep.INITIAL,
      });
    },

    /** Allow user to proceed to results/configuration when scrapper failed (no scraped data). */
    continueWithoutScrapedData() {
      patchState(store, {
        error: null,
        isLoading: false,
        currentStep: OnboardingStep.RESULTS,
      });
    },

    setStep(step: OnboardingStep) {
      patchState(store, {
        currentStep: step,
      });
    },

    updateBrandingData(data: Partial<BrandingData>) {
      const guestHomeData = store.homeGuestData();
      const newGuestHomeData = {
        headerColor: '',
        homeColor: '',
        ...store.homeGuestData(),
      };
      if (guestHomeData?.headerColor === store.brandingData().mainColor) {
        newGuestHomeData.headerColor = data.mainColor || '';
      }
      if (guestHomeData?.homeColor === store.brandingData().mainColor) {
        newGuestHomeData.homeColor = data.secondaryColor || '';
      }
      if (guestHomeData?.headerColor === store.brandingData().secondaryColor) {
        newGuestHomeData.headerColor = data.secondaryColor || '';
      }
      if (guestHomeData?.homeColor === store.brandingData().secondaryColor) {
        newGuestHomeData.homeColor = data.secondaryColor || '';
      }
      patchState(store, {
        homeGuestData: newGuestHomeData,
      });
      patchState(store, {
        brandingData: {
          ...store.brandingData(),
          ...data,
        },
      });
    },

    updateFaqs(faqs: FaqItem[]) {
      patchState(store, {
        brandingData: {
          ...store.brandingData(),
          faqs,
        },
      });
    },

    updateLogo(logo: string) {
      patchState(store, {
        brandingData: {
          ...store.brandingData(),
          logo,
        },
      });
    },

    reset() {
      patchState(store, {
        ...initialState,
        currentStep: OnboardingStep.INITIAL,
      });
    },

    // Navigation helpers
    goToInitial() {
      patchState(store, {
        currentStep: OnboardingStep.INITIAL,
      });
    },

    goToResults() {
      patchState(store, {
        currentStep: OnboardingStep.RESULTS,
      });
    },

    goToConfiguration() {
      patchState(store, {
        currentStep: OnboardingStep.CONFIGURATION,
      });
    },

    setCreatedHotelData(data: CreatedHotelData) {
      patchState(store, {
        createdHotelData: data,
      });
    },

    setHomeGuestData(data: HomeGuestData) {
      patchState(store, {
        homeGuestData: data,
      });
    },
  })),
);
